/* ─────────────────────────────────────────────────────────────────────
   Tier Discount Progress Bar — Controller

   Zwei Basis-Modi (data-basis):
     'qty'    → Stufen greifen ab Stückzahl der teilnehmenden Produkte
     'amount' → Stufen greifen ab Warenkorbwert (in Cent)

   Im amount-Modus bestimmt data-amount-source, welcher Wert gilt:
     'items_subtotal' → cart.items_subtotal_price (nach Produktrabatten,
                        VOR Staffelrabatt — stabil, empfohlen)
     'total'          → cart.total_price (nach allen Rabatten)
     'original'       → cart.original_total_price (vor allen Rabatten)
   Ist eine Produktliste gesetzt, zählt nur die Summe dieser Positionen.

   Text-Tokens:
     [percent]   → Rabattsatz der nächsten bzw. erreichten Stufe
     [qty]       → qty-Modus: fehlende Stück · amount-Modus: fehlender Betrag
     [amount]    → fehlender Betrag bis zur nächsten Stufe (formatiert)
     [threshold] → Schwelle der nächsten Stufe (formatiert bzw. Stückzahl)

   Subscribes to PUB_SUB_EVENTS.cartUpdate (pubsub.js, constants.js).
   ───────────────────────────────────────────────────────────────────── */

if (!customElements.get('tier-discount-progress')) {
  class TierDiscountProgress extends HTMLElement {
    constructor() {
      super();
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._unsub = null;
      this._inflight = null;
      this._cart = null;
    }

    connectedCallback() {
      this._parseConfig();
      this._buildMarkers();
      this._evaluate(this._initialValue);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartUpdate);
      }
    }

    disconnectedCallback() {
      if (typeof this._unsub === 'function') {
        this._unsub();
        this._unsub = null;
      }
    }

    _parseConfig() {
      this._basis = this.dataset.basis === 'amount' ? 'amount' : 'qty';
      this._amountSource = this.dataset.amountSource || 'items_subtotal';
      this._currency = this.dataset.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      this._locale = document.documentElement.lang || undefined;

      const idsRaw = (this.dataset.productIds || '').trim();
      this._productIds = idsRaw
        ? idsRaw.split(',').map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n))
        : [];

      let tiers = [];
      try {
        tiers = JSON.parse(this.dataset.tiers || '[]');
      } catch (e) {
        tiers = [];
      }

      // Je nach Basis wird qty oder amount zur maßgeblichen Schwelle.
      const key = this._basis === 'amount' ? 'amount' : 'qty';
      this._tiers = tiers
        .filter((t) => t && Number(t[key]) > 0 && Number(t.percent) > 0)
        .map((t) => ({ value: Number(t[key]), percent: Number(t.percent) }))
        .sort((a, b) => a.value - b.value);

      this._maxValue = this._tiers.length ? this._tiers[this._tiers.length - 1].value : 0;

      this._textStart = this.dataset.textStart || '';
      this._textNext = this.dataset.textNext || '';
      this._textMax = this.dataset.textMax || '';
      this._textPartial = this.dataset.textPartial || this._textMax;

      const initial = this.dataset.initialValue != null ? this.dataset.initialValue : this.dataset.initialQty;
      this._initialValue = Number(initial || 0);
      this._location = this.dataset.location || 'product';

      this._statusEl = this.querySelector('[data-tier-text]');
      this._fillEl = this.querySelector('[data-tier-fill]');
      this._markersEl = this.querySelector('[data-tier-markers]');
      this._legendItems = this.querySelectorAll('[data-tier-idx]');
    }

    _buildMarkers() {
      if (!this._markersEl || this._maxValue <= 0) return;
      this._markersEl.innerHTML = '';
      this._tiers.forEach((tier, idx) => {
        const m = document.createElement('span');
        m.className = 'tier-discount-progress__marker';
        const pct = Math.min(100, (tier.value / this._maxValue) * 100);
        m.style.left = pct + '%';
        m.dataset.tierIdx = String(idx);
        m.dataset.tierValue = String(tier.value);
        m.dataset.tierPercent = String(tier.percent);
        m.dataset.reached = 'false';
        m.setAttribute('aria-hidden', 'true');
        this._markersEl.appendChild(m);
      });
    }

    /* ── Discount-active gate ─────────────────────────────────────
       Any active discount code qualifies. Sources (any one truthy):
         • body.dataset.activeDiscountCode (set by theme.liquid)
         • body.classList.contains('discount-active')
         • discount_code cookie present
         • cart.discount_applications / cart_level_discount_applications
       ───────────────────────────────────────────────────────────── */
    _isAnyDiscountActive(activeCodesFromCart) {
      if (document.body.dataset.activeDiscountCode) return true;
      if (document.body.classList.contains('discount-active')) return true;

      const cookieMatch = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      if (cookieMatch && cookieMatch[1]) return true;

      if (Array.isArray(activeCodesFromCart) && activeCodesFromCart.length > 0) {
        return true;
      }

      return false;
    }

    /* ── Cart fetch ───────────────────────────────────────────────── */
    async _fetchCart() {
      if (this._inflight) return this._inflight;
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this._inflight = fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (cart) this._cart = cart;
          return cart;
        })
        .catch(() => null)
        .finally(() => {
          this._inflight = null;
        });
      return this._inflight;
    }

    /* ── Maßgeblicher Wert aus dem Cart ───────────────────────────── */
    _valueFromCart(cart) {
      if (!cart) return 0;

      if (this._basis === 'amount') {
        // Produktliste gesetzt → nur diese Positionen summieren.
        if (this._productIds.length && Array.isArray(cart.items)) {
          let sum = 0;
          cart.items.forEach((item) => {
            if (!this._productIds.includes(item.product_id)) return;
            sum += this._amountSource === 'original'
              ? (item.original_line_price || 0)
              : (item.final_line_price || 0);
          });
          return sum;
        }
        // Sonst ganzer Warenkorb.
        if (this._amountSource === 'total') return cart.total_price || 0;
        if (this._amountSource === 'original') return cart.original_total_price || 0;
        return cart.items_subtotal_price || 0;
      }

      if (!Array.isArray(cart.items) || !this._productIds.length) return 0;
      let total = 0;
      cart.items.forEach((item) => {
        if (this._productIds.includes(item.product_id)) {
          total += item.quantity || 0;
        }
      });
      return total;
    }

    _codesFromCart(cart) {
      if (!cart) return [];
      const codes = [];
      if (Array.isArray(cart.discount_applications)) {
        cart.discount_applications.forEach((d) => {
          if (d && d.title) codes.push(d.title);
        });
      }
      if (Array.isArray(cart.cart_level_discount_applications)) {
        cart.cart_level_discount_applications.forEach((d) => {
          if (d && d.title) codes.push(d.title);
        });
      }
      return codes;
    }

    /* ── Cart update event ────────────────────────────────────────── */
    async _onCartUpdate(event) {
      // Some sources pass full cartData; otherwise re-fetch.
      const cart = (event && event.cartData) ? event.cartData : await this._fetchCart();
      if (cart) this._cart = cart;
      const value = this._valueFromCart(cart);
      const cartCodes = this._codesFromCart(cart);
      this._evaluate(value, cartCodes);
    }

    /* ── Evaluate visibility + render ─────────────────────────────── */
    async _evaluate(initialValue, initialCodes) {
      let codes = initialCodes;

      // Cheap synchronous signals first.
      let active = this._isAnyDiscountActive(codes);

      // If nothing cheap matched, do an authoritative cart fetch.
      // This also gives us the value for the product page on first render.
      if (!active || !this._cart || (this._location === 'product' && (initialValue === 0 || codes == null))) {
        const cart = await this._fetchCart();
        if (cart) {
          codes = this._codesFromCart(cart);
          if (!initialValue) initialValue = this._valueFromCart(cart);
          active = this._isAnyDiscountActive(codes);
        }
      }

      if (!active) {
        this.hidden = true;
        return;
      }

      // Cart-drawer gate: hide if nothing counts yet
      if (this._location === 'drawer' && (!initialValue || initialValue <= 0)) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this._render(initialValue || 0);
    }

    /* ── Gleicher Scope wie _valueFromCart, aber Originalpreise ──── */
    _originalValueFromCart(cart) {
      if (!cart) return 0;
      if (this._productIds.length && Array.isArray(cart.items)) {
        let sum = 0;
        cart.items.forEach((item) => {
          if (!this._productIds.includes(item.product_id)) return;
          sum += item.original_line_price || 0;
        });
        return sum;
      }
      return cart.original_total_price || 0;
    }

    /* ── Ist-Rabatt aus dem Warenkorb ─────────────────────────────
       Der Rabattcode prueft gegen die Endsumme NACH allen Rabatten.
       Dadurch wird der Satz nur so weit gewaehrt, wie der Warenkorb
       ueber der Schwelle bleibt — der Kunde bekommt zwischen Schwelle
       und Vollrabatt also weniger als den Stufenwert. Diese Helfer
       liefern, was tatsaechlich gespart wurde und was bis zum vollen
       Satz fehlt.
       ───────────────────────────────────────────────────────────── */
    _actuals(topPercent) {
      const cart = this._cart;
      if (!cart) return null;
      const original = this._originalValueFromCart(cart);
      const total = this._valueFromCart(cart);
      if (original <= 0) return null;

      const saved = Math.max(0, original - total);
      const realPercent = Math.round((saved / original) * 100);

      const p = topPercent / 100;
      const fullSaved = Math.round(original * p);
      // Voller Satz greift erst, wenn der Warenkorb auch danach ueber
      // der Schwelle liegt: original * (1 - p) >= threshold
      const topValue = this._tiers.length ? this._tiers[this._tiers.length - 1].value : 0;
      const requiredOriginal = p < 1 ? Math.ceil(topValue / (1 - p)) : topValue;

      return {
        saved: saved,
        realPercent: realPercent,
        isFull: saved >= fullSaved - 2,        // 2 Cent Rundungstoleranz
        toFull: Math.max(0, requiredOriginal - original),
        requiredOriginal: requiredOriginal,
      };
    }

    /* ── Rendering ────────────────────────────────────────────────── */
    _render(value) {
      if (!this._tiers.length) return;

      // Determine current and next tier
      let current = null;
      let next = null;
      for (let i = 0; i < this._tiers.length; i++) {
        if (value >= this._tiers[i].value) current = this._tiers[i];
        else { next = this._tiers[i]; break; }
      }

      // Status text
      let statusText = '';
      let partial = null;
      if (current && !next) {
        const act = this._usesFinalTotal() ? this._actuals(current.percent) : null;
        if (act && act.saved > 0 && !act.isFull && act.toFull > 0) partial = act;

        statusText = this._format(partial ? this._textPartial : this._textMax, {
          percent: current.percent,
          remaining: 0,
          threshold: current.value,
          actuals: act,
        });
      } else if (next) {
        const remaining = Math.max(0, next.value - value);
        // Start-Copy nur bei wirklich leerem Cart-Anteil; sonst "Noch X bis Y%".
        const tpl = value <= 0 ? this._textStart : this._textNext;
        statusText = this._format(tpl, {
          percent: next.percent,
          remaining: remaining,
          threshold: next.value,
        });
      }
      if (this._statusEl) this._statusEl.innerHTML = statusText;

      // Fill width — linear up to last tier. Im Teilzustand laeuft die
      // Leiste stattdessen auf den Punkt zu, ab dem der volle Satz greift.
      let fillPct = this._maxValue > 0
        ? Math.max(0, Math.min(100, (value / this._maxValue) * 100))
        : 0;
      if (partial && this._cart && partial.requiredOriginal > 0) {
        const orig = this._originalValueFromCart(this._cart);
        fillPct = Math.max(0, Math.min(100, (orig / partial.requiredOriginal) * 100));
      }
      if (this._fillEl) this._fillEl.style.width = fillPct + '%';

      // Marker + legend reached state
      const markers = this._markersEl
        ? this._markersEl.querySelectorAll('.tier-discount-progress__marker')
        : [];
      markers.forEach((m) => {
        m.dataset.reached = value >= Number(m.dataset.tierValue) ? 'true' : 'false';
      });
      if (this._legendItems) {
        const attr = this._basis === 'amount' ? 'tierAmount' : 'tierQty';
        this._legendItems.forEach((li) => {
          li.dataset.reached = value >= Number(li.dataset[attr]) ? 'true' : 'false';
        });
      }
    }

    /* ── Helpers ──────────────────────────────────────────────────── */
    _money(cents) {
      try {
        return new Intl.NumberFormat(this._locale, {
          style: 'currency',
          currency: this._currency,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2).replace('.', ',') + ' €';
      }
    }

    _display(value) {
      return this._basis === 'amount' ? this._money(value) : String(value);
    }

    _usesFinalTotal() {
      if (this._basis !== 'amount') return false;
      // Mit Produktliste rechnet _valueFromCart mit final_line_price,
      // also bereits nach Rabatt — ausser die Quelle ist explizit 'original'.
      if (this._productIds.length) return this._amountSource !== 'original';
      return this._amountSource === 'total';
    }

    _format(tpl, tokens) {
      if (!tpl) return '';
      const remaining = this._display(tokens.remaining);
      const threshold = this._display(tokens.threshold);
      const act = tokens.actuals;
      if (act) {
        tpl = tpl
          .replace(/\[saved\]/g, '<strong>' + this._money(act.saved) + '</strong>')
          .replace(/\[realpercent\]/g, '<strong>' + act.realPercent + '</strong>')
          .replace(/\[tofull\]/g, '<strong>' + this._money(act.toFull) + '</strong>');
      }
      tpl = tpl
        .replace(/\[saved\]/g, '')
        .replace(/\[realpercent\]/g, '')
        .replace(/\[tofull\]/g, '');
      return tpl
        .replace(/\[qty\]/g, '<strong>' + remaining + '</strong>')
        .replace(/\[amount\]/g, '<strong>' + remaining + '</strong>')
        .replace(/\[threshold\]/g, '<strong>' + threshold + '</strong>')
        .replace(/\[percent\]/g, '<strong>' + tokens.percent + '</strong>');
    }
  }

  customElements.define('tier-discount-progress', TierDiscountProgress);
}